import {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import DmnModeler from "dmn-js/lib/Modeler";
import { toast } from "sonner";
import "dmn-js/dist/assets/diagram-js.css";
import "dmn-js/dist/assets/dmn-js-decision-table-controls.css";
import "dmn-js/dist/assets/dmn-js-decision-table.css";
import "dmn-js/dist/assets/dmn-js-drd.css";
import "dmn-js/dist/assets/dmn-js-literal-expression.css";
import "dmn-js/dist/assets/dmn-js-shared.css";
import "dmn-js/dist/assets/dmn-font/css/dmn.css";
import {
  Plus,
  Minus,
  Download,
} from "lucide-react";

import {
  getDecisionContent,
  saveDecisionContent,
  decodeDmnXML,
  emptyDmnTemplate,
} from "../../services/decisionService";
import type { Decision } from "../../services/decisionService";

interface DmnEditorProps {
  decision?: Decision | null;
  onSave?: (decision: Decision) => void;
}

export interface DmnEditorRef {
  save: () => Promise<void>;
  getXml: () => Promise<string | undefined>;
  importXml: (xml: string) => Promise<void>;
  exportXml: () => void;
  isSaving: boolean;
}

const DmnEditor = forwardRef<DmnEditorRef, DmnEditorProps>(
  ({ decision, onSave }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const modelerRef = useRef<DmnModeler | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [zoomLevel, setZoomLevel] = useState(100);

    // Get the slug to use for API calls
    const getSlug = (): string | null => {
      if (decision?.nameSlug) {
        return decision.nameSlug;
      }
      if (decision?.name) {
        return decision.name.toLowerCase().replace(/\s+/g, "_");
      }
      return null;
    };

    // Initialize modeler
    useEffect(() => {
      let isMounted = true;
      
      const initializeEditor = async () => {
        if (!containerRef.current) {
          console.warn("DmnEditor: Container ref not available, retrying...");
          setTimeout(initializeEditor, 100);
          return;
        }
        
        if (!isMounted) return;

        console.log("DmnEditor: Starting initialization...");

        try {
          // Destroy previous modeler if exists
          if (modelerRef.current) {
            modelerRef.current.destroy();
            modelerRef.current = null;
          }

          // Create DMN modeler
          const modeler = new DmnModeler({
            container: containerRef.current,
            drd: {
              propertiesPanel: {
                parent: '#dmn-properties-panel'
              }
            },
            common: {
              keyboard: {
                bindTo: document
              }
            },
            // Disable dmn-js logo
            additionalModules: [
              {
                __init__: ["disablePoweredBy"],
                disablePoweredBy: [
                  "type",
                  function () {
                    return function () {
                      /* Disable logo */
                    };
                  },
                ],
              },
            ],
          });

          modelerRef.current = modeler;
          console.log("DmnEditor: Modeler created");

          // Determine DMN XML to load
          const slug = getSlug();

          let dmnXml = emptyDmnTemplate(decision?.name || "New Decision");

          // Try to load existing DMN content
          if (slug) {
            try {
              console.log(`DmnEditor: Loading decision content for ${slug}`);
              const content = await getDecisionContent(slug);
              if (content?.dmnXml) {
                const decoded = decodeDmnXML(content.dmnXml);
                if (decoded) {
                  dmnXml = decoded;
                  console.log("DmnEditor: Loaded existing DMN content");
                }
              }
            } catch (err) {
              console.log("DmnEditor: No existing content, using template");
            }
          }

          // Import DMN XML
          console.log("DmnEditor: Importing DMN XML...");
          await modeler.importXML(dmnXml);
          console.log("DmnEditor: DMN imported successfully");

          // Get active view and zoom to fit
          try {
            const activeView = modeler.getActiveView();
            if (activeView && activeView.get) {
              const canvas = activeView.get("canvas");
              if (canvas && canvas.zoom) {
                canvas.zoom("fit-viewport");
                const currentZoom = canvas.zoom();
                setZoomLevel(Math.round(currentZoom * 100));
              }
            }
          } catch (zoomErr) {
            console.warn("DmnEditor: Could not set zoom:", zoomErr);
          }

          if (isMounted) {
            setIsLoading(false);
            setError(null);
          }
        } catch (err) {
          console.error("DmnEditor: Error initializing modeler:", err);
          if (isMounted) {
            setError(err instanceof Error ? err.message : "Failed to initialize DMN editor");
            setIsLoading(false);
          }
        }
      };

      // Start initialization after a small delay to ensure DOM is ready
      const timer = setTimeout(initializeEditor, 100);

      return () => {
        isMounted = false;
        clearTimeout(timer);
        if (modelerRef.current) {
          modelerRef.current.destroy();
          modelerRef.current = null;
        }
      };
    }, [decision?.id]);

    // Save function
    const save = async (): Promise<void> => {
      if (!modelerRef.current) {
        toast.error("DMN editor not initialized");
        return;
      }

      const slug = getSlug();
      if (!slug) {
        toast.error("Decision slug not available");
        return;
      }

      setIsSaving(true);
      try {
        const { xml } = await modelerRef.current.saveXML({ format: true });
        if (!xml) {
          throw new Error("Failed to generate DMN XML");
        }

        const version = decision?.version || "0.1";
        await saveDecisionContent(slug, xml, version);

        toast.success("Decision saved successfully");
        if (onSave && decision) {
          onSave(decision);
        }
      } catch (err) {
        console.error("Error saving decision:", err);
        toast.error(err instanceof Error ? err.message : "Failed to save decision");
        throw err;
      } finally {
        setIsSaving(false);
      }
    };

    // Get XML function
    const getXml = async (): Promise<string | undefined> => {
      if (!modelerRef.current) return undefined;
      try {
        const { xml } = await modelerRef.current.saveXML({ format: true });
        return xml;
      } catch (err) {
        console.error("Error getting XML:", err);
        return undefined;
      }
    };

    // Import XML function
    const importXml = async (xml: string): Promise<void> => {
      if (!modelerRef.current) {
        throw new Error("DMN editor not initialized");
      }
      await modelerRef.current.importXML(xml);
      
      // Zoom to fit after import
      const activeView = modelerRef.current.getActiveView();
      if (activeView) {
        const canvas = activeView.get("canvas");
        if (canvas && canvas.zoom) {
          canvas.zoom("fit-viewport");
          const currentZoom = canvas.zoom();
          setZoomLevel(Math.round(currentZoom * 100));
        }
      }

      // Force canvas height after import
      setTimeout(() => {
        const container = containerRef.current;
        if (container) {
          const dmnElements = container.querySelectorAll('.dmn-js-parent, .dmn-js, .dmn-js .canvas, .dmn-js-container');
          dmnElements.forEach(el => {
            (el as HTMLElement).style.height = '100%';
          });
        }
      }, 100);
    };

    // Export XML function
    const exportXml = async (): Promise<void> => {
      const xml = await getXml();
      if (!xml) {
        toast.error("Failed to export DMN");
        return;
      }

      const blob = new Blob([xml], { type: "text/xml" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${decision?.nameSlug || "decision"}.dmn`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("DMN exported successfully");
    };

    // Zoom functions
    const handleZoomIn = () => {
      if (!modelerRef.current) return;
      const activeView = modelerRef.current.getActiveView();
      if (activeView) {
        const canvas = activeView.get("canvas");
        if (canvas && canvas.zoom) {
          const newZoom = canvas.zoom() * 1.2;
          canvas.zoom(newZoom);
          setZoomLevel(Math.round(newZoom * 100));
        }
      }
    };

    const handleZoomOut = () => {
      if (!modelerRef.current) return;
      const activeView = modelerRef.current.getActiveView();
      if (activeView) {
        const canvas = activeView.get("canvas");
        if (canvas && canvas.zoom) {
          const newZoom = canvas.zoom() / 1.2;
          canvas.zoom(newZoom);
          setZoomLevel(Math.round(newZoom * 100));
        }
      }
    };

    const handleZoomReset = () => {
      if (!modelerRef.current) return;
      const activeView = modelerRef.current.getActiveView();
      if (activeView) {
        const canvas = activeView.get("canvas");
        if (canvas && canvas.zoom) {
          canvas.zoom("fit-viewport");
          const currentZoom = canvas.zoom();
          setZoomLevel(Math.round(currentZoom * 100));
        }
      }
    };

    // Force DMN canvas to use full height
    useEffect(() => {
      const forceCanvasHeight = () => {
        const container = containerRef.current;
        if (container) {
          const dmnElements = container.querySelectorAll('.dmn-js-parent, .dmn-js, .dmn-js .canvas, .dmn-js-container');
          dmnElements.forEach(el => {
            (el as HTMLElement).style.height = '100%';
          });
        }
      };

      // Apply immediately and then on a delay to ensure DMN-js is rendered
      forceCanvasHeight();
      const timer = setTimeout(forceCanvasHeight, 500);
      const timer2 = setTimeout(forceCanvasHeight, 1000);

      return () => {
        clearTimeout(timer);
        clearTimeout(timer2);
      };
    }, [isLoading]);
    useImperativeHandle(ref, () => ({
      save,
      getXml,
      importXml,
      exportXml,
      isSaving,
    }));

    return (
      <div className="relative h-full w-full flex flex-col bg-white">
        {/* Loading Overlay */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-50">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading DMN editor...</p>
            </div>
          </div>
        )}

        {/* Error Overlay */}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-50">
            <div className="text-center">
              <p className="text-red-600 mb-2">Error loading DMN editor</p>
              <p className="text-sm text-gray-600">{error}</p>
            </div>
          </div>
        )}

        {/* Main Editor UI */}
        <div className="relative h-full w-full flex flex-col bg-white">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">
              {decision?.name || "Decision Editor"}
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Zoom Controls */}
            <div className="flex items-center gap-1 border border-gray-300 rounded-md bg-white">
              <button
                onClick={handleZoomOut}
                className="p-1.5 hover:bg-gray-100 transition-colors"
                title="Zoom Out"
              >
                <Minus size={16} />
              </button>
              <button
                onClick={handleZoomReset}
                className="px-2 py-1.5 text-xs font-medium hover:bg-gray-100 transition-colors border-x border-gray-300"
                title="Reset Zoom"
              >
                {zoomLevel}%
              </button>
              <button
                onClick={handleZoomIn}
                className="p-1.5 hover:bg-gray-100 transition-colors"
                title="Zoom In"
              >
                <Plus size={16} />
              </button>
            </div>

            {/* Export Button */}
            <button
              onClick={exportXml}
              className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
              title="Export DMN"
            >
              <Download size={16} />
              Export
            </button>
          </div>
        </div>

        {/* DMN Canvas */}
        <div className="flex-1 relative overflow-hidden">
          <div ref={containerRef} className="h-full w-full" style={{ height: '100%', width: '100%' }} />
        </div>

        {/* Properties Panel (hidden div for DMN properties) */}
        <div id="dmn-properties-panel" className="hidden" />
        </div>
      </div>
    );
  }
);

DmnEditor.displayName = "DmnEditor";

export default DmnEditor;
